import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../../Common/Loading";


type SchoolCode = "ALL" | "AA" | "DLR_KE_AU" | "ISMARTSTUD" | "TWIZ";

interface ReportRow {
  slNo: number;
  surname: string;
  phoneNumber: string;
  dealer: string;
  loginId: string;
  student: string;
  dateOfLastPmd: string; // "dd-MM-yyyy HH:mm:ss" or ""
}

type SortableKey = "slNo" | "surname" | "student" | "dateOfLastPmd";
type SortDirection = "asc" | "desc";

interface ColumnDef {
  key: keyof ReportRow;
  label: string;
  sortable: boolean;
}

const COLUMNS: ColumnDef[] = [
  { key: "slNo", label: "SL No", sortable: true },
  { key: "surname", label: "Surname", sortable: true },
  { key: "phoneNumber", label: "Phone Number", sortable: false },
  { key: "dealer", label: "Dealer", sortable: false },
  { key: "loginId", label: "Login ID", sortable: false },
  { key: "student", label: "Student", sortable: true },
  { key: "dateOfLastPmd", label: "Date of last PMD", sortable: true },
];

const SCHOOL_CODE_OPTIONS: SchoolCode[] = ["ALL", "AA", "DLR_KE_AU", "ISMARTSTUD", "TWIZ"];

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

const SESSION_KEYS_TO_CLEAR = ["SES_PreviousMenuPage"];

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultToDate(): string {
  return toDateInputValue(new Date());
}

function defaultFromDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return toDateInputValue(d);
}

// Mirrors parseDateDMY: expects "dd-MM-yyyy HH:mm:ss".
function parseDateDMY(value: string): number | null {
  if (!value) return null;
  const [datePart, timePart] = value.split(" ");
  const dateBits = datePart?.split("-");
  if (!dateBits || dateBits.length !== 3) return null;

  const day = parseInt(dateBits[0], 10);
  const month = parseInt(dateBits[1], 10) - 1;
  const year = parseInt(dateBits[2], 10);

  let hour = 0;
  let minute = 0;
  let second = 0;
  if (timePart) {
    const timeBits = timePart.split(":");
    hour = parseInt(timeBits[0], 10) || 0;
    minute = parseInt(timeBits[1], 10) || 0;
    second = parseInt(timeBits[2], 10) || 0;
  }

  const dt = new Date(year, month, day, hour, minute, second);
  return isNaN(dt.getTime()) ? null : dt.getTime();
}

const ProgressMDReport: React.FC = () => {
  const navigate = useNavigate();

  const [schoolCode, setSchoolCode] = useState<SchoolCode>("DLR_KE_AU");
  const [fromDate, setFromDate] = useState<string>(defaultFromDate());
  const [toDate, setToDate] = useState<string>(defaultToDate());

  const [packages, setPackages] = useState<string[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<Set<string>>(new Set());

  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string>("");
  const [sortKey, setSortKey] = useState<SortableKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const runReport = useCallback(
    async (sc: SchoolCode, from: string, to: string, pkgs: Set<string>) => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          school_code: sc,
          from_date: from,
          to_date: to,
          packages: Array.from(pkgs).join(","),
        });
        const resp = await fetch(`${API_BASE}api/reports/progress-md/?${params.toString()}`, {
          credentials: "include",
        });
        if (!resp.ok) {
          throw new Error(`Request failed with status ${resp.status}`);
        }
        const data: { rows: ReportRow[] } = await resp.json();
        setRows(data.rows);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load report");
        setRows([]);
      } finally {
        setLoading(false);
        setHasSearched(true);
      }
    },
    []
  );

  const loadPackages = useCallback(
    async (sc: SchoolCode, from: string, to: string) => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ school_code: sc });
        const resp = await fetch(
          `${API_BASE}api/reports/progress-md/packages/?${params.toString()}`,
          { credentials: "include" }
        );
        if (!resp.ok) {
          throw new Error(`Request failed with status ${resp.status}`);
        }
        const data: { packages: string[] } = await resp.json();
        setPackages(data.packages);

        // Mirrors: chkPackages.Items(0).Selected = True, then LoadReport().
        const defaultSelection = new Set<string>(data.packages.length > 0 ? [data.packages[0]] : []);
        setSelectedPackages(defaultSelection);
        await runReport(sc, from, to, defaultSelection);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load packages");
        setPackages([]);
        setSelectedPackages(new Set());
        setLoading(false);
      }
    },
    [runReport]
  );

  // Mirrors Page_Load's !IsPostBack branch: default school/dates, load
  // packages for the default school, which in turn runs the first report.
  useEffect(() => {
    loadPackages(schoolCode, fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSchoolCodeChange = (value: SchoolCode) => {
    setSchoolCode(value);
    loadPackages(value, fromDate, toDate);
  };

  const handleGoClick = () => {
    runReport(schoolCode, fromDate, toDate, selectedPackages);
  };

  const togglePackage = (pkg: string) => {
    setSelectedPackages((prev) => {
      const next = new Set(prev);
      if (next.has(pkg)) {
        next.delete(pkg);
      } else {
        next.add(pkg);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedPackages(checked ? new Set(packages) : new Set());
  };

  const handleSort = (key: SortableKey) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };


const handleRowClick = (row: ReportRow) => {
  const url =
    `../../Support/EL_AddEditTask?FamilyCode=A` +
    `&SchoolCode=${encodeURIComponent(row.dealer)}` +
    `&Login=${encodeURIComponent(row.loginId)}` +
    `&FilterType=CustEntryTut&S=A&F=AA`;

  window.open(
    url,
    "_blank",
    "width=1400,height=900,resizable=yes,scrollbars=yes"
  );
};


  const handleClose = () => {
    SESSION_KEYS_TO_CLEAR.forEach((key) => sessionStorage.removeItem(key));
    navigate("/SysAdmin");
  };

  // Client-side search across every column's displayed text, then sort.
  const visibleRows = useMemo(() => {
    const filter = searchText.trim().toLowerCase();
    let result = rows;
    if (filter) {
      result = rows.filter((row) =>
        COLUMNS.some((col) => String(row[col.key]).toLowerCase().includes(filter))
      );
    }

    if (sortKey) {
      result = [...result].sort((a, b) => {
        const va = a[sortKey];
        const vb = b[sortKey];

        if (va === "" && vb === "") return 0;
        if (va === "") return 1;
        if (vb === "") return -1;

        if (sortKey === "slNo") {
          const diff = (va as number) - (vb as number);
          return sortDirection === "asc" ? diff : -diff;
        }

        if (sortKey === "dateOfLastPmd") {
          const da = parseDateDMY(String(va));
          const db = parseDateDMY(String(vb));
          if (da !== null && db !== null) {
            return sortDirection === "asc" ? da - db : db - da;
          }
        }

        const cmp = String(va).localeCompare(String(vb), undefined, {
          numeric: true,
          sensitivity: "base",
        });
        return sortDirection === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [rows, searchText, sortKey, sortDirection]);

  const showNoRecords = hasSearched && !loading && !error && visibleRows.length === 0;
  const showTable = !loading && !error;
  const allSelected = packages.length > 0 && selectedPackages.size === packages.length;

  const sortIcon = (key: SortableKey) => {
    if (sortKey !== key) return " ↕";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className="pmd-report">
      {/* ===== HEADER ===== */}
      <div className="pmd-header">
        <div className="pmd-title">Progress MD Report</div>
        <button type="button" className="pmd-btn pmd-btn-light" onClick={handleClose}>
          Close
        </button>
      </div>

      <div className="pmd-card">
        {/* ===== SEARCH BAR ===== */}
        <div className="pmd-search-bar">
          <label htmlFor="ddlSchoolCode">SchoolCode:</label>
          <select
            id="ddlSchoolCode"
            className="pmd-select"
            value={schoolCode}
            onChange={(e) => handleSchoolCodeChange(e.target.value as SchoolCode)}
            disabled={loading}
          >
            {SCHOOL_CODE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>

          <label htmlFor="txtTableSearch">Search:</label>
          <input
            id="txtTableSearch"
            type="text"
            className="pmd-input"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <label htmlFor="txtFromDate">From:</label>
          <input
            id="txtFromDate"
            type="date"
            className="pmd-input pmd-date-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            disabled={loading}
          />

          <label htmlFor="txtToDate">To:</label>
          <input
            id="txtToDate"
            type="date"
            className="pmd-input pmd-date-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            disabled={loading}
          />

          <button
            type="button"
            className="pmd-btn pmd-btn-primary"
            onClick={handleGoClick}
            disabled={loading}
          >
            Go
          </button>
        </div>

        {/* ===== BODY: package panel + table ===== */}
        <div className="pmd-body">
          <div className="pmd-package-panel">
            <div className="pmd-package-header">
              <label className="pmd-package-label">Packages:</label>
              <label className="pmd-select-all">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
                Select All
              </label>
            </div>

            <table className="pmd-package-list">
              <tbody>
                {packages.map((pkg) => (
                  <tr key={pkg}>
                    <td>
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedPackages.has(pkg)}
                          onChange={() => togglePackage(pkg)}
                        />
                        {pkg}
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pmd-table-wrapper">
            {loading && (
              <div className="pmd-loading-wrap">
                <Loading />
              </div>
            )}

            {!loading && error && <div className="pmd-alert pmd-alert-danger">{error}</div>}

            {showTable && (
              <table id="exportTable">
                <thead>
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        onClick={col.sortable ? () => handleSort(col.key as SortableKey) : undefined}
                        className={col.sortable ? "pmd-sortable" : ""}
                      >
                        {col.label}
                        {col.sortable && (
                          <span className="pmd-sort-icon">{sortIcon(col.key as SortableKey)}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={`${row.dealer}-${row.loginId}-${row.slNo}`} onClick={() => handleRowClick(row)}>
                      <td>{row.slNo}</td>
                      <td>{row.surname}</td>
                      <td>{row.phoneNumber}</td>
                      <td>{row.dealer}</td>
                      <td>{row.loginId}</td>
                      <td>{row.student}</td>
                      <td>{row.dateOfLastPmd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {showNoRecords && <div className="pmd-alert pmd-alert-warning">No records found</div>}
          </div>
        </div>
      </div>

      {/* ===== IN-FILE CSS — no Bootstrap or any external stylesheet required ===== */}
      <style>{`
        .pmd-report {
          font-family: Arial, Helvetica, sans-serif;
          width: 100%;
          height: 100vh;
          padding: 8px 14px;
          box-sizing: border-box;
          background-color: #f4f6f9;
        }
        .pmd-report *, .pmd-report *::before, .pmd-report *::after {
          box-sizing: border-box;
        }

        .pmd-header {
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 18px;
          margin-bottom: 15px;
          background: linear-gradient(45deg, #008080, #0d6efd);
          color: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 7px rgba(0, 0, 0, 0.12);
        }
        .pmd-title {
          font-size: 18px;
          font-weight: 600;
          letter-spacing: 0.2px;
        }

        .pmd-card {
          background: #fff;
          border: 1px solid #dfe3e8;
          border-radius: 10px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          height: calc(100vh - 88px);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .pmd-search-bar {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 15px;
          background: #fff;
          border-bottom: 1px solid #dfe3e8;
          flex-wrap: wrap;
        }
        .pmd-search-bar label {
          font-weight: 600;
          white-space: nowrap;
          margin: 0;
        }
        .pmd-select, .pmd-input {
          padding: 6px 10px;
          border: 1px solid #ced4da;
          border-radius: 6px;
          font-size: 14px;
        }
        .pmd-input {
          width: 260px;
          max-width: 100%;
        }
        .pmd-date-input {
          width: 160px;
        }

        .pmd-btn {
          padding: 7px 18px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          border: 1px solid transparent;
          cursor: pointer;
        }
        .pmd-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        .pmd-btn-primary {
          background: #0d6efd;
          border-color: #0d6efd;
          color: #fff;
        }
        .pmd-btn-primary:hover:not(:disabled) {
          background: #0b5ed7;
        }
        .pmd-btn-light {
          background: #fff;
          border-color: #fff;
          color: #212529;
        }
        .pmd-btn-light:hover {
          background: #e9ecef;
        }

        .pmd-body {
          display: flex;
          flex: 1;
          min-height: 0;
          gap: 10px;
        }

        .pmd-package-panel {
          flex-shrink: 0;
          width: 260px;
          background: #fff;
          border-right: 1px solid #dfe3e8;
          padding: 10px;
          overflow-y: auto;
        }
        .pmd-package-header {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 8px;
        }
        .pmd-package-label {
          font-weight: 600;
          margin: 0;
        }
        .pmd-select-all {
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          cursor: pointer;
        }
        .pmd-package-list {
          width: 100%;
          border-collapse: collapse;
        }
        .pmd-package-list td {
          padding: 4px 2px;
          font-size: 14px;
          white-space: nowrap;
        }
        .pmd-package-list label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
        }

        .pmd-table-wrapper {
          width: 100%;
          flex: 1;
          min-height: 0;
          padding: 10px;
          overflow: auto;
        }

        #exportTable {
          border-collapse: collapse;
          border-spacing: 0;
          margin: 0;
          padding: 0;
          width: auto;
          table-layout: auto;
          white-space: nowrap;
        }
        #exportTable thead th {
          position: sticky;
          top: 0;
          z-index: 1;
          background-color: #343a40;
          color: #fff;
          border: 1px solid #202428;
          padding: 10px;
          font-size: 16px;
          font-family: "Times New Roman", Times, serif;
          font-weight: bold;
          text-align: left;
          white-space: nowrap;
        }
        #exportTable .pmd-sortable {
          cursor: pointer;
        }
        #exportTable tbody td {
          border: 1px solid #343a40;
          padding: 5px;
          font-size: 16px;
          font-family: "Times New Roman", Times, serif;
          color: #222;
          background-color: #fff;
          white-space: nowrap;
        }
        #exportTable tbody tr {
          cursor: pointer;
        }
        #exportTable tbody tr:nth-child(even) td {
          background-color: #fafafa;
        }
        #exportTable tbody tr:hover td {
          background-color: #eef6ff;
        }
        #exportTable th:first-child,
        #exportTable td:first-child {
          text-align: center;
        }

        .pmd-loading-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 0;
        }

        .pmd-alert {
          margin: 12px 0;
          padding: 10px 16px;
          border-radius: 8px;
          text-align: center;
          font-size: 14px;
        }
        .pmd-alert-danger {
          background: #f8d7da;
          color: #842029;
          border: 1px solid #f5c2c7;
        }
        .pmd-alert-warning {
          background: #fff3cd;
          color: #664d03;
          border: 1px solid #ffecb5;
        }

        @media (max-width: 768px) {
          .pmd-report { padding: 8px; }
          .pmd-header { height: 46px; margin-bottom: 10px; padding: 0 14px; }
          .pmd-title { font-size: 16px; }
          .pmd-card { height: calc(100vh - 72px); }
          .pmd-body { flex-direction: column; }
          .pmd-package-panel { width: 100%; border-right: none; border-bottom: 1px solid #dfe3e8; }
        }
      `}</style>
    </div>
  );
};

export default ProgressMDReport;